import { useRouter } from "next/navigation";
import React, {
  createContext,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";

import useMediaQuery from "@/app/common-hooks/use-media-query/use-media-query";
import useResetStateTravelerEdit from "@/app/common-hooks/use-reset-state-traveler-edit/user-reset-state-traveler-edit";
import {
  TravelDates,
  TravelDatePageContent,
  GroupedTravelDatesMagnoliaDataType,
  keyMap,
  TripType,
  SelectionType,
  TravelDatesModalType,
} from "@/app/components/travel-dates/types/travel-dates.type";
import { useWizard } from "@/app/shared-providers/wizard-context-provider";
import { ANNUAL_MULTI_TRIP, SINGLE_TRIP } from "@/constants";
import { RootState } from "@/store/root-reducer";

import { returnCalculateDate } from "../utils/date-picker.utils";
import { groupedTravelDatesMagnoliaData } from "../utils/travel-dates.utils";

export interface TravelDatesProviderValue {
  // Getters
  cancelBtnClick: boolean;
  groupedMagnoliaData: GroupedTravelDatesMagnoliaDataType;
  isCalenderOpen: boolean;
  isDateError: boolean;
  isDrawerOpen: boolean;
  isMobile: boolean;
  isModalOpen: boolean;
  isOpenUnsavedChangesModal: boolean;
  keyForGroupedMagnolia: keyof GroupedTravelDatesMagnoliaDataType;
  magnoliaData: TravelDatePageContent;
  maxDateForReturnST: Date;
  maxDateforAMT: Date;
  maxFromDate: Date;
  modalType: TravelDatesModalType;
  pendingDate: Date | null;
  quotePolicyType: string | undefined;
  scrollRefArray: Array<{
    ref: React.RefObject<HTMLDivElement | null>;
    isToggleWrapper: boolean;
  }>;
  toggleWrapperRef: React.RefObject<HTMLDivElement | null>;
  travelDateErrorMessage: string;
  travelDates: TravelDates;
  toLocation: string;
  fromLocation: string;

  // Setters
  handleUnSavedWarningMessageContinueBtn: () => void;
  setCancelBtnClick: React.Dispatch<React.SetStateAction<boolean>>;
  setError: (msg: string) => void;
  setIsCalenderOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setIsDateError: React.Dispatch<React.SetStateAction<boolean>>;
  setIsDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setIsOpenUnsavedChangesModal: React.Dispatch<React.SetStateAction<boolean>>;
  setModalType: React.Dispatch<React.SetStateAction<TravelDatesModalType>>;
  setPendingDate: React.Dispatch<React.SetStateAction<Date | null>>;
  setTravelDateErrorMessage: React.Dispatch<React.SetStateAction<string>>;
  setTravelDates: React.Dispatch<React.SetStateAction<TravelDates>>;
}

export const TravelDatesContext = createContext<
  TravelDatesProviderValue | undefined
>(undefined);

function TravelDatesProvider({
  children,
  initialValue,
}: Readonly<{
  children: React.ReactNode;
  initialValue: TravelDatePageContent;
}>) {
  const router = useRouter();
  const { current } = useWizard();
  const { resetQuestionData } = useResetStateTravelerEdit();
  const toggleWrapperRef = useRef<HTMLDivElement>(null);

  const attributes = useSelector((state: RootState) => state.quote?.attributes);
  const quotePolicyType = attributes?.tripDetails?.policyType;

  const toLocationAMT = attributes?.tripDetails?.area as string;
  const toLocationST = attributes?.tripDetails?.toLocation as string;

  const toLocation =
    quotePolicyType === ANNUAL_MULTI_TRIP ? toLocationAMT : toLocationST;

  const fromLocation =
    (current.brandSpecificParams?.fromCountry as string) ??
    attributes?.tripDetails?.fromLocation;

  const [isMobile, isReady] = useMediaQuery("(max-width: 600px)");

  const [travelDates, setTravelDates] = useState<TravelDates>({
    validate: false,
    startDate: null,
    endDate: null,
  });
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [isOpenUnsavedChangesModal, setIsOpenUnsavedChangesModal] =
    useState<boolean>(false);
  const [isDateError, setIsDateError] = useState(false);
  const [travelDateErrorMessage, setTravelDateErrorMessage] = useState("");
  const [modalType, setModalType] = useState<"SameLocation" | "SameDay" | null>(
    null
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [cancelBtnClick, setCancelBtnClick] = useState<boolean>(false);
  const [isCalenderOpen, setIsCalenderOpen] = useState(false);
  const [pendingDate, setPendingDate] = useState<Date | null>(null);

  const tripType: TripType =
    quotePolicyType === ANNUAL_MULTI_TRIP ? ANNUAL_MULTI_TRIP : SINGLE_TRIP;

  const selection: SelectionType = useMemo(
    () =>
      travelDates.startDate === null ||
      travelDates.endDate === null ||
      travelDates.validate === false
        ? "DEFAULT"
        : "SELECTED",
    [travelDates]
  );

  const keyForGroupedMagnolia = keyMap[tripType][selection];

  const handleUnSavedWarningMessageContinueBtn = useCallback(() => {
    setIsOpenUnsavedChangesModal(false);
    resetQuestionData();
    router.push("/");
  }, [resetQuestionData, router]);

  const groupedMagnoliaData = useMemo(() => {
    return groupedTravelDatesMagnoliaData(initialValue);
  }, [initialValue]);

  const scrollRefArray = useMemo(() => {
    return [
      {
        ref: toggleWrapperRef,
        isToggleWrapper: true,
      },
    ];
  }, [toggleWrapperRef]);

  const maxFromDate = useMemo(() => {
    const maxDate = returnCalculateDate(
      new Date(),
      null,
      null,
      current?.brandSpecificParams?.maxDateRange as number
    );
    return maxDate;
  }, [current]);

  const maxDateForReturnST = useMemo(() => {
    const baseDate = travelDates.startDate ? travelDates.startDate : new Date();
    const maxDate = returnCalculateDate(
      baseDate,
      null,
      null,
      current.brandSpecificParams?.maxDateRange as number
    );
    return maxDate;
  }, [travelDates, current.brandSpecificParams?.maxDateRange]);

  const maxDateforAMT = useMemo(() => {
    const maxDate = returnCalculateDate(new Date(), null, null, 89);
    return maxDate;
  }, []);

  const setError = useCallback((msg: string) => {
    setIsDateError(true);
    setTravelDateErrorMessage(msg);
  }, []);

  const contextValue = React.useMemo(
    () => ({
      // Getters
      cancelBtnClick,
      groupedMagnoliaData,
      isCalenderOpen,
      isDateError,
      isDrawerOpen,
      isMobile,
      isModalOpen,
      isOpenUnsavedChangesModal,
      keyForGroupedMagnolia,
      magnoliaData: initialValue,
      maxDateForReturnST,
      maxDateforAMT,
      maxFromDate,
      modalType,
      pendingDate,
      quotePolicyType,
      scrollRefArray,
      toggleWrapperRef,
      travelDateErrorMessage,
      travelDates,
      toLocation,
      fromLocation,

      // Setters
      handleUnSavedWarningMessageContinueBtn,
      setCancelBtnClick,
      setError,
      setIsCalenderOpen,
      setIsDateError,
      setIsDrawerOpen,
      setIsModalOpen,
      setIsOpenUnsavedChangesModal,
      setModalType,
      setPendingDate,
      setTravelDateErrorMessage,
      setTravelDates,
    }),
    [
      // Getters
      cancelBtnClick,
      groupedMagnoliaData,
      isCalenderOpen,
      isDateError,
      isDrawerOpen,
      isMobile,
      isModalOpen,
      isOpenUnsavedChangesModal,
      keyForGroupedMagnolia,
      initialValue,
      maxDateForReturnST,
      maxDateforAMT,
      maxFromDate,
      modalType,
      pendingDate,
      quotePolicyType,
      scrollRefArray,
      toggleWrapperRef,
      travelDateErrorMessage,
      travelDates,
      toLocation,
      fromLocation,

      // Setters
      handleUnSavedWarningMessageContinueBtn,
      setCancelBtnClick,
      setError,
      setIsCalenderOpen,
      setIsDateError,
      setIsDrawerOpen,
      setIsModalOpen,
      setIsOpenUnsavedChangesModal,
      setModalType,
      setPendingDate,
      setTravelDateErrorMessage,
      setTravelDates,
    ]
  );

  if (!isReady) return null;
  return (
    <TravelDatesContext.Provider value={contextValue}>
      {children}
    </TravelDatesContext.Provider>
  );
}

export const useTravelDatesContext = () => {
  const context = React.useContext(TravelDatesContext);
  if (!context) {
    throw new Error(
      "useTravelDatesContext must be used within a TravelDatesProvider"
    );
  }
  return context;
};

export default TravelDatesProvider;
